import {applyMiddleware, createStore} from 'redux';
import {thunk} from 'redux-thunk';
import {persistStore, persistReducer} from 'redux-persist';
import rootReducer from '../reducers';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {E_STORAGE_KEYS} from '../../@types';
import {composeWithDevTools} from 'redux-devtools-extension';
import isTokenExpired from '../../utils/isTokenExpired';
import {reportError} from '../../utils/reportError';

// redux-persist expects state reconciliation to stay synchronous during launch.
const stateReconciler = (inboundState: any, originalState: any, reducedState: any) => {
  // If there's no inbound state, return the reduced state (initial state)
  if (!inboundState) {
    return reducedState;
  }

  // Check if the inbound state has an auth token
  const authToken = inboundState?.auth?.token?.token;

  // If no token exists, continue with normal rehydration
  if (!authToken) {
    return {...reducedState, ...inboundState};
  }

  try {
    if (isTokenExpired(authToken)) {
      console.log('Persisted auth token is expired. Skipping auth rehydration.');
      return {
        ...reducedState,
        ...inboundState,
        auth: reducedState.auth,
      };
    }
  } catch (error) {
    reportError(error, {
      feature: 'redux',
      action: 'validate token during rehydration',
    });

    return {
      ...reducedState,
      ...inboundState,
      auth: reducedState.auth,
    };
  }

  const finalState = {...reducedState, ...inboundState};
  console.log('Redux rehydration complete. Units in state:', finalState?.units?.unitLinks?.length || 0);
  return finalState;
};


const persistConfig = {
  key: E_STORAGE_KEYS.REDUX_PERSIST_ROOT,
  storage: AsyncStorage,
  stateReconciler,
  whitelist: ['units', 'auth', 'people', 'approvals', 'alerts', 'register', 'notificationSound'], // Persist onboarding drafts so Settings hops do not lose form state
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = createStore(
  persistedReducer,
  composeWithDevTools(
    applyMiddleware(thunk)
  )
);

export const persistor = persistStore(store, {}, () => {
  // Callback when persist is complete
  console.log('✅ Redux-Persist rehydration complete');
});

// Debug: Log persist subscribe events
persistor.subscribe(() => {
  const state = store.getState();
  const unitsCount = state?.units?.unitLinks?.length || 0;
  console.log('Redux state changed. Units now:', unitsCount);
});

const configureStore = () => {
  return store;
};

const _store = configureStore();

export type RootStore = ReturnType<typeof _store.getState>;
export type AppDispatch = typeof _store.dispatch;

export default configureStore;
